import { BreadCrumb } from 'primereact/breadcrumb';
import { Column } from 'primereact/column';
import { DataTable, DataTableExpandedRows, DataTableStateEvent, DataTableValueArray } from 'primereact/datatable';
import { Toolbar } from 'primereact/toolbar';
import { useDebounce } from 'primereact/hooks';
import { FC, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { AssignmentsQueryComponent } from './AssignmentsQueryComponent';
import { AssignmentsFilters, AssignmentStatusFilter } from './AssignmentsFilters';
import { PageInfo } from '../../models/PageInfo';
import { AssignmentTableRowContent } from './AssignmentTableRowContent';
import { AssignmentItemList } from '../../models/Assignment';
import {
  AssignmentUpdateMutation,
  AssignmentUpdateMutation$variables,
} from '../../graphQLOperations/__generated__/AssignmentUpdateMutation.graphql';
import { cancelAssignment, updateAssignment } from '../../graphQLOperations/Assignment';
import { useMutation } from 'react-relay';
import { Toast } from 'primereact/toast';
import { AssignmentCancelMutation } from '../../graphQLOperations/__generated__/AssignmentCancelMutation.graphql';
import { AssignmentsFilterInput } from '../../graphQLOperations/__generated__/AssignmentGetAssignmentsQuery.graphql';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { formatClassDisplayName } from '../../utils/formatClassDisplayName';

export interface PageControlAssignments {
  first: number;
  rows: number;
  page?: number;
  pageCount: number;
  sortOrder: 1 | -1;
  totalPages: number;
  multiSortMeta: null;
  prevPage: number;
}

const pageControlInitState: PageControlAssignments = {
  first: 0,
  rows: 10,
  page: 0,
  pageCount: 0,
  sortOrder: 1,
  totalPages: 1,
  multiSortMeta: null,
  prevPage: 0,
};

export const TeacherAssignments: FC = () => {
  const navigate = useNavigate();
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows | DataTableValueArray | undefined>(undefined);
  const [assignments, setAssignments] = useState<AssignmentItemList[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [pageInfo, setPageInfo] = useState<PageInfo | undefined>(undefined);
  const [firstLoad, setFirstLoad] = useState<boolean>(true);
  const [fetchingData, setFetchingData] = useState<boolean>(true);
  const [pageControl, setPageControl] = useState<PageControlAssignments>(pageControlInitState);
  const [search, debouncedSearch, setSearch] = useDebounce<string>('', 500);
  const [status, setStatus] = useState<AssignmentStatusFilter>('ALL');
  const [selectedClassIds, setSelectedClassIds] = useState<string[]>([]);
  const [selectedSkillIds, setSelectedSkillIds] = useState<string[]>([]);
  const [countsRefreshKey, setCountsRefreshKey] = useState<number>(0);
  const toastRef = useRef<Toast>(null);

  const countsFilter = useMemo<AssignmentsFilterInput>(
    () => ({
      isActive: 'ACTIVE',
      ...(debouncedSearch.trim() ? { nameContains: debouncedSearch.trim() } : {}),
      ...(selectedClassIds.length ? { classIds: selectedClassIds } : {}),
      ...(selectedSkillIds.length ? { skillIds: selectedSkillIds } : {}),
    }),
    [debouncedSearch, selectedClassIds, selectedSkillIds],
  );

  const filters = useMemo<AssignmentsFilterInput>(
    () => (status === 'ACTIVE' ? { ...countsFilter, dueOnFrom: new Date() } : countsFilter),
    [countsFilter, status],
  );

  useEffect(() => {
    setFetchingData(true);
    setPageControl(pageControlInitState);
  }, [filters]);

  const breadcrumbItems = [{ label: 'Monitor', command: () => navigate('/quick-data') }, { label: 'Assignments' }];
  const [updateAssignmentMutation] = useMutation<AssignmentUpdateMutation>(updateAssignment);
  const [cancelAssignmentMutation] = useMutation<AssignmentCancelMutation>(cancelAssignment);
  const updateAssignmentHandler = (variables: AssignmentUpdateMutation$variables) => {
    updateAssignmentMutation({
      variables,
      onCompleted() {
        toastRef.current?.show({
          severity: 'success',
          summary: 'Your changes to the assignment have been saved.',
          icon: 'nc_icon-nc_confirm icon-sm',
        });
        setFetchingData(true);
        setPageControl(pageControlInitState);
        setCountsRefreshKey(key => key + 1);
      },
    });
  };
  const cancelAssignmentHandler = (assignmentId: string) => {
    cancelAssignmentMutation({
      variables: {
        assignmentId,
      },
      onCompleted() {
        toastRef.current?.show({
          severity: 'success',
          summary: 'Assignment has been deleted.',
          icon: 'nc_icon-nc_confirm icon-sm',
        });
        setFetchingData(true);
        setPageControl(pageControlInitState);
        setCountsRefreshKey(key => key + 1);
      },
    });
  };
  return (
    <div className="grid nc_content-frame nc_page-inner pb-8">
      <Toast position="bottom-center" ref={toastRef} />
      <div className="col-10 col-offset-1 nc_assignments-page-header">
        <BreadCrumb
          className="nc_breadcrumb"
          model={breadcrumbItems}
          home={{ icon: 'nc_icon-nc_home icon-sm', url: '/' }}
        />
        <Toolbar className="nc_page-header" center={<h1>Assignments</h1>} />
      </div>
      <div className="col-10 col-offset-1 grid nc_block">
        <Suspense>
          <AssignmentsFilters
            search={search}
            onSearchChange={setSearch}
            status={status}
            onStatusChange={setStatus}
            countsFilter={countsFilter}
            countsRefreshKey={countsRefreshKey}
            selectedClassIds={selectedClassIds}
            onSelectedClassIdsChange={setSelectedClassIds}
            selectedSkillIds={selectedSkillIds}
            onSelectedSkillIdsChange={setSelectedSkillIds}
          />
        </Suspense>
        <DataTable
          dataKey="id"
          expandedRows={expandedRows}
          onRowToggle={e => setExpandedRows(e.data)}
          rowExpansionTemplate={item => (
            <AssignmentTableRowContent
              key={item.id}
              cancelAssignmentHandler={cancelAssignmentHandler}
              updateAssignmentHandler={updateAssignmentHandler}
              assignment={item}
            />
          )}
          className="w-full"
          lazy
          loading={fetchingData}
          stripedRows
          paginator
          rows={pageControl.rows}
          rowsPerPageOptions={[5, 10, 25]}
          totalRecords={totalRecords}
          sortOrder={pageControl.sortOrder}
          filterDisplay="row"
          emptyMessage="No assignments found"
          value={assignments}
          scrollable
          first={pageControl.first}
          onPage={(e: DataTableStateEvent) => {
            setFetchingData(true);
            const newPage: PageControlAssignments = {
              ...pageControl,
              page: e.page,
              rows: e.rows,
              first: e.first,
              pageCount: e.pageCount!,
              prevPage: pageControl.page!,
            };
            setPageControl(newPage);
          }}
          onSort={() => {
            setFetchingData(true);
            setPageControl(prev => ({ ...pageControlInitState, sortOrder: prev.sortOrder === -1 ? 1 : -1 }));
          }}
          paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
        >
          <Column expander={true} bodyClassName="text-600 w-1rem" />
          <Column
            header="Name"
            field="name"
            bodyClassName="text-lg font-medium text-700"
            style={{ minWidth: '15vw' }}
          />
          <Column
            header="Class"
            body={(item: AssignmentItemList) => {
              const classItem = item.classes?.[0];
              return <p className="text-600 font-medium">{classItem ? formatClassDisplayName(classItem) : '-'}</p>;
            }}
          />
          <Column
            header="Target Skill"
            body={item => <p className="text-600 font-medium">{`${item.skill.name} (${item.skill.code})`}</p>}
          />
          <Column
            sortable
            header="Due Date"
            body={(item: AssignmentItemList) => <p className="text-600 font-medium">{format(item.dueOn, 'P')}</p>}
          />
        </DataTable>
      </div>
      <Suspense>
        <AssignmentsQueryComponent
          filters={filters}
          pageControl={pageControl}
          setAssignments={setAssignments}
          setTotalRecords={setTotalRecords}
          setFetchingData={setFetchingData}
          setFirstLoad={setFirstLoad}
          setPageInfo={setPageInfo}
          pageInfo={pageInfo}
          fetchingData={fetchingData}
          firstLoad={firstLoad}
        />
      </Suspense>
    </div>
  );
};
